import { useQuery, useMutation, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient, InfiniteData } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useAuth } from '../store/auth';
import { api, serverNow, type MessageContent } from '../api';
import type { Session, SessionMessage, Paginated } from '../api';

type SessionsPage = Paginated<Session, string>;
type SessionsCache = InfiniteData<SessionsPage>;

const SEEKER_KEY: readonly unknown[] = ['sessions', 'mine', 'seeker'];
const AVATAR_KEY: readonly unknown[] = ['sessions', 'mine', 'avatar'];

function rankMs(s: Session): number {
  return Number.isFinite(s.rankedAt) ? s.rankedAt : 0;
}

function sortByRanked(list: Session[]): Session[] {
  return [...list].sort((a, b) => rankMs(b) - rankMs(a));
}

/**
 * Apply a batch of upsert/drop ops to one paginated inbox cache in a
 * single setQueryData call. Strategy mirrors the link inbox: flatten
 * loaded pages, splice each fresh row in, re-sort once at the end,
 * put everything back into page 0; emptied tail pages are kept so
 * cursor-based load-more still chains from the original boundary.
 *
 * Single-row writers funnel through here with a one-element batch.
 */
function upsertIntoSessionsCache(
  qc: QueryClient,
  key: readonly unknown[],
  ops: Array<{ session: Session; shouldKeep: boolean }>,
) {
  if (ops.length === 0) return;
  qc.setQueryData<SessionsCache>(key, (old) => {
    if (!old?.pages.length) return old;
    let all = old.pages.flatMap(p => p.items);
    let touched = false;
    let needsSort = false;
    for (const { session, shouldKeep } of ops) {
      const i = all.findIndex(s => s.id === session.id);
      if (shouldKeep) {
        if (i >= 0) all = all.map((s, j) => (j === i ? { ...s, ...session } : s));
        else        all = [session, ...all];
        needsSort = true;
        touched = true;
      } else if (i >= 0) {
        all = all.filter((s) => s.id !== session.id);
        touched = true;
      }
    }
    if (!touched) return old;
    if (needsSort) all = sortByRanked(all);
    const [first, ...rest] = old.pages;
    return {
      ...old,
      pages: [
        { ...first, items: all },
        ...rest.map(p => ({ ...p, items: [] as Session[] })),
      ],
    };
  });
}

/**
 * Pure cache writer for the participant side of session lifecycle.
 *
 * Slot routing (the user's role on this session):
 *   - I'm the seeker  → seeker cache, hidden when deletedSeekerAt is set
 *   - I'm the owner   → avatar cache, hidden when the session has
 *                       expired (`expiredAt` is the canonical stamp,
 *                       set in lockstep with the avatar's by the BE)
 *
 * Also updates the per-id cache `['sessions', sessionId]` so single-
 * session views see fresh state. Navigation is **not** done here —
 * pages that care subscribe to the relevant cache slice and decide
 * based on their own context.
 */
export function processMySession(qc: QueryClient, session: Session, currentUserId: string) {
  processMySessionList(qc, [session], currentUserId);
}

/**
 * Batched flavor — apply N session updates with a single setQueryData
 * per inbox cache (one re-render of /messages and /avatar instead of
 * N). The per-id and per-id-messages caches still get one write each
 * because the keys are distinct, but their observers are typically
 * scoped to the open chat — at most one mounts at a time.
 */
export function processMySessionList(qc: QueryClient, sessions: Session[], currentUserId: string) {
  if (sessions.length === 0) return;
  const seekerOps: Array<{ session: Session; shouldKeep: boolean }> = [];
  const avatarOps: Array<{ session: Session; shouldKeep: boolean }> = [];
  for (const session of sessions) {
    const isSeeker = session.seekerId === currentUserId;
    const shouldKeep = isSeeker ? !session.deletedSeekerAt : !session.expiredAt;
    (isSeeker ? seekerOps : avatarOps).push({ session, shouldKeep });
  }
  upsertIntoSessionsCache(qc, SEEKER_KEY, seekerOps);
  upsertIntoSessionsCache(qc, AVATAR_KEY, avatarOps);

  type MsgPage = { session: Session; messages: import('../api').SessionMessage[] };
  for (const session of sessions) {
    qc.setQueryData<Session>(['sessions', session.id], (old) =>
      old ? { ...old, ...session } : session,
    );
    // Also update the session embedded in the messages cache so the chat
    // page sees fresh linkRequest / readonly / linkedAt without a separate
    // fetch.
    qc.setQueryData<{ pages: MsgPage[]; pageParams: unknown[] }>(
      ['sessions', session.id, 'messages'],
      (old) => {
        if (!old?.pages.length) return old;
        const pages = old.pages.slice();
        const last = pages[pages.length - 1];
        pages[pages.length - 1] = { ...last, session: { ...last.session, ...session } };
        return { ...old, pages };
      },
    );
  }
}

/**
 * Hook flavor — captures qc and current user id; returns a stable
 * bound function.
 */
export function useProcessMySession() {
  const qc = useQueryClient();
  const userId = useAuth(s => s.user?.id);
  return useCallback((session: Session) => {
    if (!userId) return;
    processMySession(qc, session, userId);
  }, [qc, userId]);
}

export function useProcessMySessionList() {
  const qc = useQueryClient();
  const userId = useAuth(s => s.user?.id);
  return useCallback((sessions: Session[]) => {
    if (!userId) return;
    processMySessionList(qc, sessions, userId);
  }, [qc, userId]);
}

export function useMySeekerSessions() {
  return useInfiniteQuery({
    queryKey: SEEKER_KEY,
    queryFn: ({ pageParam }) => api.sessions.seekerMine({ next: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next ?? undefined,
  });
}

export function useMyAvatarSessions() {
  return useInfiniteQuery({
    queryKey: AVATAR_KEY,
    queryFn: ({ pageParam }) => api.sessions.avatarMine({ next: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next ?? undefined,
  });
}

export { SEEKER_KEY as SESSIONS_SEEKER_KEY, AVATAR_KEY as SESSIONS_AVATAR_KEY };

export function useSession(id: string) {
  return useQuery({
    queryKey: ['sessions', id],
    queryFn: () => api.sessions.get(id),
    enabled: !!id,
    // The cache is also updated by processMySession on socket events and
    // by useSessionMessages on every page load, so a small staleTime is
    // enough to dedupe the initial render without going stale.
    staleTime: 5_000,
  });
}

// Probe a chat from the seeker side. No DB write; returns the avatar
// and the existing session if one is already open. Stale time short so
// the countdown stays fresh on re-entry.
export function useChatPreview(avatarId: string) {
  return useQuery({
    queryKey: ['sessions', 'open', avatarId],
    queryFn: () => api.sessions.open(avatarId),
    enabled: !!avatarId,
    staleTime: 5_000,
  });
}

// Lazy first-message-by-avatar: creates the session server-side. The
// returned session goes through processMySession, which is the only
// writer of ['sessions','mine'] outside the initial fetch.
export function useStartMessage() {
  const handle = useProcessMySession();
  return useMutation({
    mutationFn: ({ avatarId, content }: { avatarId: string; content: MessageContent }) =>
      api.sessions.startMessage(avatarId, content),
    onSuccess: ({ session }) => handle(session),
  });
}

export function useSessionMessages(sessionId: string) {
  const qc = useQueryClient();
  const userId = useAuth(s => s.user?.id);
  return useInfiniteQuery({
    queryKey: ['sessions', sessionId, 'messages'],
    // pageParam = µs cursor for older messages (the BE's `next` field).
    // Initial load passes undefined → BE returns the latest batch.
    queryFn: async ({ pageParam }) => {
      const result = await api.sessions.getMessages(sessionId, { next: pageParam });
      // Mirror the freshly-decorated session into the per-id cache and
      // /messages slices, so the chat page (which reads from useSession)
      // and the inbox views all stay in sync without an extra round-trip.
      if (userId) processMySession(qc, result.session, userId);
      return result;
    },
    initialPageParam: undefined as number | undefined,
    // Older messages (scroll-up) live in the "next" direction.
    // BE's `next` field is null when we've hit the bottom of history.
    getPreviousPageParam: (firstPage) => firstPage.next ?? undefined,
    getNextPageParam: () => undefined,
    enabled: !!sessionId,
    // Disable every form of TanStack auto-refetch. Older pages already
    // loaded via fetchPreviousPage are immutable history, no point
    // re-fetching them. New messages catch-up uses an explicit
    // `?prev=<max>` flow in the chat page, not TanStack's refetch.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });
}

// Seeker-side soft delete: stamps deletedSeekerAt server-side; the
// returned session goes through processMySession which removes it from
// asSeeker on the caller and emits to other devices via the socket.
export function useDeleteSession() {
  const handle = useProcessMySession();
  return useMutation({
    mutationFn: (id: string) => api.sessions.delete(id),
    onSuccess: (deleted) => handle(deleted),
  });
}

/**
 * Caller-perspective unread check. The BE projects `lastMessageAt`
 * (the OTHER side's latest send) and `lastReadAt` (my watermark)
 * into the Session payload from my POV.
 */
export function sessionHasUnread(session: Session): boolean {
  return session.lastMessageAt > session.lastReadAt;
}

export function useRestoreSession() {
  const handle = useProcessMySession();
  return useMutation({
    mutationFn: (id: string) => api.sessions.restore(id),
    onSuccess: (restored) => handle(restored),
  });
}

type MsgPage = { session: Session; messages: SessionMessage[]; next: number | null; prev: number | null };
type MsgCache = { pages: MsgPage[]; pageParams: unknown[] };

/**
 * Optimistic message send. Appends a `_pending` placeholder to the
 * messages cache before the request goes out, swaps it for the real
 * row on success, removes it on failure. The placeholder uses
 * `serverNow() * 1000` for a µs-scale createdAt so ordering with
 * concurrently-arriving partner messages stays sane while in-flight.
 */
export function useSendSessionMessage(sessionId: string) {
  const qc = useQueryClient();
  const handle = useProcessMySession();
  const userId = useAuth(s => s.user?.id);
  return useMutation({
    mutationFn: (content: MessageContent) => api.sessions.sendMessage(sessionId, content),

    // Cancel any in-flight refetches that could race with the
    // optimistic write, then append the placeholder.
    onMutate: async (content) => {
      await qc.cancelQueries({ queryKey: ['sessions', sessionId, 'messages'] });
      const tempId = `temp:${Math.random().toString(36).slice(2, 10)}:${Date.now()}`;
      const session = qc.getQueryData<Session>(['sessions', sessionId]);
      const fromSeeker = !!userId && session?.seekerId === userId;
      const optimistic: SessionMessage = {
        id: tempId,
        sessionId,
        fromSeeker,
        content,
        createdAt: serverNow() * 1000,
        _pending: true,
      };
      qc.setQueryData<MsgCache>(['sessions', sessionId, 'messages'], (old) => {
        if (!old?.pages.length) return old;
        const pages = old.pages.slice();
        const last = pages[pages.length - 1];
        pages[pages.length - 1] = { ...last, messages: [...last.messages, optimistic] };
        return { ...old, pages };
      });
      return { tempId };
    },

    // Replace the placeholder with the real row, bump the inbox card.
    // Other devices receive the same payload via `newMessage`.
    onSuccess: ({ session, message }, _vars, ctx) => {
      handle(session);
      qc.setQueryData<MsgCache>(['sessions', sessionId, 'messages'], (old) => {
        if (!old?.pages.length) return old;
        const tempId = ctx?.tempId;
        let touched = false;
        const pages = old.pages.map(p => {
          const idx = tempId ? p.messages.findIndex(m => m.id === tempId) : -1;
          if (idx >= 0) {
            touched = true;
            const messages = p.messages.slice();
            messages[idx] = message;
            return { ...p, messages };
          }
          return p;
        });
        // No placeholder found (e.g. cache cleared mid-flight): append
        // the real message to the last page so the chat stays correct.
        if (!touched) {
          const last = pages[pages.length - 1];
          pages[pages.length - 1] = { ...last, messages: [...last.messages, message] };
        }
        return { ...old, pages };
      });
    },

    // Drop the placeholder so the chat doesn't show a phantom bubble.
    // The page-level catch handles draft restore + toast.
    onError: (_err, _vars, ctx) => {
      const tempId = ctx?.tempId;
      if (!tempId) return;
      qc.setQueryData<MsgCache>(['sessions', sessionId, 'messages'], (old) => {
        if (!old?.pages.length) return old;
        const pages = old.pages.map(p => ({
          ...p,
          messages: p.messages.filter(m => m.id !== tempId),
        }));
        return { ...old, pages };
      });
    },
  });
}
